import React from "react";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { addUserApi, updateUserApi, fetchUserByIdApi } from "../api/user";

interface UserFormProps {
  url: string;
  userId: string | null;
  onClose: () => void;
  onSaved?:() => void;
}

export const UserForm = ({ url, userId, onClose , onSaved }: UserFormProps) => {
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [address, setAddress] = useState("");
  const [roleType, setRoleType] = useState("");
  const [gender, setGender] = useState("");

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [, setUid] = useState<string>("");

  const validateForm = () => {
    if (!userName || !roleType) {
      toast.error(
        "Please fill in all required fields: User Name, Email, and Role Type."
      );
      return false;
    }
    return true;
  };

  const id = userId;

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (id) {
          const fetchedUser = await fetchUserByIdApi(url, id);
          if (fetchedUser) {
            const user = fetchedUser as {
              name: string;
              email: string;
              mobile: string;
              address: string;
              roleType: string;
              gender: string;
            };
            setIsEditMode(true);
            setLoading(false);
            setUserName(user.name);
            setEmail(user.email);
            setMobile(user.mobile);
            setAddress(user.address);
            setRoleType(user.roleType);
            setGender(user.gender);
          }
        }
      } catch (error) {
        console.error("Error fetching User:", error);
        toast.error("Failed to fetch the User.", { id: "fetchUserError" });
        setHasError(true);
        setLoading(false);
      }
    };

    if (id) {
      fetchUserData();
    }
  }, [id, url]);

  useEffect(() => {
    if (id) {
      setUid(id);
      setIsEditMode(true);
      setLoading(true);
    } else {
      setIsEditMode(false);
      setLoading(false);
    }
  }, [id]);

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      if (isEditMode) {
        await updateUserApi(
          url,
          id,
          userName,
          email,
          mobile,
          address,
          roleType,
          gender
        );
        toast.success("User updated successfully");
      } else {
        await addUserApi(
          url,
          userName,
          email,
          mobile,
          address,
          roleType,
          gender
        );
        toast.success("User added successfully");
      }
      
      // window.history.pushState({}, "", "/admin/users");
      // window.dispatchEvent(new PopStateEvent("popstate"));
      if(onSaved) onSaved();
      onClose();
      
    } catch (error) {
      console.error("Error adding/updating User:", error);
      toast.error(
        isEditMode ? "Failed to Update the User." : "Failed to Add the User."
      );
    }
  };

  const handleCancelClick = () => {
    setUserName("");
    setEmail("");
    setMobile("");
    setAddress("");
    setRoleType("");
    setGender("");
    onClose();
  };

  return (
    <div className="">
      <div>
        {loading ? (
          <div className="flex justify-center items-center h-screen">
            <div
              className="flex justify-center items-center h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] text-blue-600"
              role="status"
            >
              <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                Loading...
              </span>
            </div>
          </div>
        ) : hasError ? (
          <div className="flex justify-center items-center h-screen">
            {/* Blank page when there’s an error */}
          </div>
        ) : (
          <div>
            <form>
              <h1 className="text-3xl font-bold text-blue-600 capitalize dark:text-white mb-4">
                {isEditMode ? "Edit User" : "Add User"}
              </h1>

              <div className="mb-6 w-full">
                <label className="text-gray-900 dark:text-gray-200 font-semibold">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  type="text"
                  className="w-full px-4 py-2 mt-2  text-gray-800  bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                  placeholder="Enter User Name"
                />
              </div>
              <div className="mb-6 w-full">
                <label className="text-gray-900 dark:text-gray-200 font-semibold">
                  Email
                </label>
                <input
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  className="w-full px-4 py-2 mt-2  text-gray-800  bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                  placeholder="Enter Email"
                />
              </div>
              <div className="mb-6 w-full">
                <label className="text-gray-900 dark:text-gray-200 font-semibold">
                  Mobile Number
                </label>
                <input
                  required
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  type="number"
                  className="w-full px-4 py-2 mt-2  text-gray-800  bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                  placeholder="Enter Mobile Number"
                />
              </div>
              <div className="mb-6 w-full">
                <label className="text-gray-900 dark:text-gray-200 font-semibold">
                  Address
                </label>
                <input
                  required
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  type="text"
                  className="w-full px-4 py-2 mt-2  text-gray-800  bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                  placeholder="Address"
                />
              </div>

              <div className="mb-6 w-full">
                <label className="text-gray-900 dark:text-gray-200 font-semibold">
                  Gender
                </label>
                <select
                  required
                  value={gender}
                  onChange={(e) =>
                    setGender(e.target.value === "" ? null : e.target.value)
                  }
                  className="w-full px-4 py-2 mt-2  text-gray-800  bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                >
                  <option value="">Select a Gender</option>
                  <option value="1">Male</option>
                  <option value="2">Female</option>
                </select>
              </div>

              <div className="mb-6 w-full">
                <label className="text-gray-900 dark:text-gray-200 font-semibold">
                  Role Type <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={roleType}
                  onChange={(e) => setRoleType(e.target.value)}
                  className="w-full px-4 py-2 mt-2  text-gray-800  bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                >
                  <option value="">Select a Role Type</option>
                  <option value="1">Guest</option>
                  <option value="2">Admin</option>
                </select>
              </div>

              <div className="flex space-x-4 mt-6 justify-start">
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="px-8 py-2.5 leading-5 text-white transition-colors duration-300 transform bg-blue-600 rounded-md hover:bg-blue-500 focus:outline-none focus:bg-gray-600"
                >
              Save and Close
                </button>
                <button
                  type="button"
                  onClick={handleCancelClick}
                  className="px-8 py-2.5 leading-5 text-white transition-colors duration-300 transform bg-blue-600 rounded-md hover:bg-blue-500 focus:outline-none focus:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
